// Deals Dashboard — types matching docs/deals-ui-integration.md exactly.
// The dashboard response supplies its own human-readable `label` fields for
// the pipeline/board, so nothing here hardcodes display copy for those —
// only the stage-move dropdown (an action list, not response data) gets a
// small generic formatter for its option text.

export type DealStage = "new" | "qualified" | "proposal" | "negotiation" | "follow_up" | "won" | "lost";

export type BoardStage = Exclude<DealStage, "lost">;

export const STAGE_ACTIONS: DealStage[] = ["new", "qualified", "proposal", "negotiation", "follow_up", "won", "lost"];

export const stageLabel = (s: DealStage) =>
  s
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// person.source comes through as a raw token like "journey_outbound" —
// prettify it for display without inventing a fixed label map.
export const formatSource = (s: string) =>
  s
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Compact money for the tight board cards ("₹1,399,000.00" → "₹1.4M").
// Currency always comes from the API record, never hardcoded.
export const formatMoneyCompact = (value: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return `${value}`;
  }
};

export interface MoneyMetric {
  amount: string; // fixed-point decimal; never parse as an integer
  currency: string;
}

export interface DealPerson {
  type: "contact" | "lead";
  id: string;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  source: string | null;
  location: string | null;
  avatar_url: string | null;
}

export interface DealBoardItem {
  id: string;
  tenant_id: string;
  contact_id: string | null;
  lead_id: string | null;
  conversation_id: string | null;
  assigned_to_user_id: string | null;
  title: string;
  description: string | null;
  product_or_service: string | null;
  amount: string | null;
  currency: string | null;
  stage: DealStage;
  expected_close_at: string | null;
  won_at: string | null;
  lost_at: string | null;
  loss_reason: string | null;
  metadata: Record<string, unknown>;
  transaction: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  person: DealPerson;
}

export interface DealBoardColumn {
  stage: BoardStage;
  label: string;
  total: number;
  has_more: boolean;
  results: DealBoardItem[];
}

export interface DealsDashboard {
  generated_at: string;
  value_currency: string;
  summary: {
    new_deals: number;
    qualified_deals: number;
    proposals_sent: number;
    average_deal_value: MoneyMetric;
    potential_revenue: MoneyMetric;
  };
  pipeline: Array<{
    stage: BoardStage;
    label: string;
    count: number;
    percentage_of_new_deals: number | null;
  }>;
  board: DealBoardColumn[];
}
